/**
 * AddLeadDialog — create a single lead from a LinkedIn profile URL.
 *
 * When "Run AI enrichment" is checked, the dialog stays open through the
 * entire create → enrich pipeline. We deliberately don't invalidate the leads
 * list, surface the new lead, or open the detail panel until enrichment is
 * done — otherwise the user sees the half-populated card and a "Run
 * Enrichment" button for the 10-20s the AI call takes.
 */

import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Loader2, Search as SearchIcon, HelpCircle, ExternalLink } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useApi } from '@/hooks/use-api'
import { linkedInStatusKey } from '@/hooks/use-linkedin-status'
import { leadKeys } from './use-leads'
import { enrichmentKeys, type EnrichmentResult } from './detail/use-lead-detail'
import { normalizeLead, type BackendLead } from './normalize'
import type { Lead } from './types'

type AddLeadDialogProps = {
  readonly open: boolean
  readonly onOpenChange: (open: boolean) => void
  readonly onLeadCreated?: (lead: Lead) => void
}

type Stage = 'idle' | 'creating' | 'enriching' | 'importing'

type UrlKind = 'profile' | 'search' | 'invalid'

function classifyLinkedInUrl(value: string): UrlKind {
  try {
    const parsed = new URL(value.trim())
    if (!parsed.hostname.includes('linkedin.com')) return 'invalid'
    const path = parsed.pathname.toLowerCase()
    if (
      path.includes('/search/results/people') ||
      path.includes('/sales/search/people') ||
      path.includes('/sales/lists/people')
    ) return 'search'
    if (path.includes('/in/') || path.includes('/sales/lead/')) return 'profile'
    // A bare linkedin.com host is not enough — we need to know which kind.
    return 'invalid'
  } catch {
    return 'invalid'
  }
}

export function AddLeadDialog({ open, onOpenChange, onLeadCreated }: AddLeadDialogProps) {
  const apiFetch = useApi()
  const queryClient = useQueryClient()

  const [url, setUrl] = useState('')
  const [touched, setTouched] = useState(false)
  // Research always runs after creating (owner directive 2026-09-17: no
  // opt-in checkbox; every added lead is enriched automatically).
  const withEnrichment = true
  const [stage, setStage] = useState<Stage>('idle')
  const [error, setError] = useState<string | null>(null)
  const [importSummary, setImportSummary] = useState<{
    readonly created: number
    readonly restored?: number
    readonly already_existed: number
    /** Leads researched right after a search-URL import (research toggle on). */
    readonly researched?: number
    readonly research_note?: string
  } | null>(null)

  const trimmed = url.trim()
  const urlKind = classifyLinkedInUrl(trimmed)
  const looksValid = trimmed.length > 0 && urlKind !== 'invalid'
  const isSearchUrl = urlKind === 'search'
  const showError = touched && trimmed.length > 0 && !looksValid
  const busy = stage !== 'idle'

  function reset() {
    setUrl('')
    setTouched(false)
    setStage('idle')
    setError(null)
    setImportSummary(null)
  }

  function handleOpenChange(next: boolean) {
    if (busy) return // can't dismiss mid-flow; backdrop click is also blocked
    if (!next) reset()
    onOpenChange(next)
  }

  async function handleSubmit() {
    setTouched(true)
    if (!looksValid || busy) return
    setError(null)
    setImportSummary(null)

    // Search-URL path — bulk import every profile on the LinkedIn search
    // results page via the worker. Enrichment toggle is ignored here; we
    // surface a summary instead of opening a single-lead detail panel.
    if (isSearchUrl) {
      setStage('importing')
      try {
        const res = await apiFetch<{
          created: number
          restored?: number
          already_existed: number
          leads?: readonly BackendLead[]
          errors?: readonly string[]
          reconnect_needed?: boolean
          note?: string
        }>('/api/leads/import-search-url', {
          method: 'POST',
          body: { search_url: trimmed },
        })
        if (!res.success || !res.data) throw new Error(res.error ?? 'Import failed')

        // Credential failure path — backend flipped linkedin_accounts.status.
        // Force the LinkedIn-status query to refetch so the page's gate
        // flips to the clean "Reconnect LinkedIn" view, then surface the
        // friendly message and bail out before claiming success.
        if (res.data.reconnect_needed) {
          await queryClient.invalidateQueries({ queryKey: linkedInStatusKey })
          setError(res.data.note ?? 'Your LinkedIn connection has expired. Please reconnect LinkedIn in Settings.')
          setStage('idle')
          return
        }

        await queryClient.invalidateQueries({ queryKey: leadKeys.lists() })
        await queryClient.invalidateQueries({ queryKey: leadKeys.stats() })
        await queryClient.invalidateQueries({ queryKey: ['dashboard'] as const })

        // 2026-09-05: the research toggle used to be ignored (and hidden) on
        // search-URL imports — the owner ticked it and nothing happened.
        // Now the freshly imported rows go through the bulk research
        // endpoint, which skips already-researched leads for free.
        let researched: number | undefined
        let researchNote: string | undefined
        const importedIds = (res.data.leads ?? []).map((l) => l.id).filter((id): id is string => typeof id === 'string')
        if (withEnrichment && importedIds.length > 0) {
          setStage('enriching')
          try {
            const bulk = await apiFetch<{ enriched: number; fresh: number; failed: number; rate_limited: number; stopped_reason: string | null }>(
              '/api/ai/enrich/bulk',
              { method: 'POST', body: { lead_ids: importedIds.slice(0, 25) } },
            )
            if (bulk.success && bulk.data) {
              researched = bulk.data.enriched
              const notes: string[] = []
              if (importedIds.length > 25) notes.push(`first 25 of ${importedIds.length} researched; run "Research" on the rest from the pipeline`)
              if (bulk.data.rate_limited > 0 && bulk.data.stopped_reason) notes.push(bulk.data.stopped_reason)
              if (bulk.data.failed > 0) notes.push(`${bulk.data.failed} could not be researched`)
              researchNote = notes.length > 0 ? notes.join('. ') : undefined
              await queryClient.invalidateQueries({ queryKey: leadKeys.lists() })
            } else {
              researchNote = bulk.error ?? 'Research did not run; use "Research" from the pipeline.'
            }
          } catch (err) {
            researchNote = err instanceof Error ? err.message : 'Research did not run; use "Research" from the pipeline.'
          }
        }

        setImportSummary({
          created: res.data.created ?? 0,
          restored: res.data.restored,
          already_existed: res.data.already_existed ?? 0,
          ...(researched !== undefined ? { researched } : {}),
          ...(researchNote ? { research_note: researchNote } : {}),
        })
        setStage('idle')
        // Leave the dialog open so the user can read the summary; close
        // automatically after a short pause if everything succeeded
        // cleanly with no errors to surface.
        if ((!res.data.errors || res.data.errors.length === 0) && !researchNote) {
          setTimeout(() => {
            onOpenChange(false)
            reset()
          }, 2200)
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Import failed')
        setStage('idle')
      }
      return
    }

    // 1. Create the lead. We call the API directly instead of useCreateLeadByUrl
    // so we can defer the lists/stats invalidation until after enrichment.
    setStage('creating')
    let lead: Lead
    try {
      const res = await apiFetch<BackendLead>('/api/leads', {
        method: 'POST',
        body: { linkedin_url: trimmed, source: 'manual' },
      })
      if (!res.success || !res.data) throw new Error(res.error ?? 'Failed to create lead')
      lead = normalizeLead(res.data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create lead')
      setStage('idle')
      return
    }

    // 2. If enrichment is requested, run it and wait. Failure here doesn't
    // block the lead from appearing — it exists in DB regardless — but we
    // do surface the error in the dialog before closing.
    if (withEnrichment) {
      setStage('enriching')
      try {
        const enrichRes = await apiFetch<{ enrichment: EnrichmentResult }>('/api/ai/enrich', {
          method: 'POST',
          body: {
            lead_id: lead.id,
            linkedin_url: lead.linkedin_url,
            name: lead.full_name ?? `${lead.first_name ?? ''} ${lead.last_name ?? ''}`.trim(),
            company: lead.company ?? '',
            title: lead.job_title ?? '',
          },
        })
        if (!enrichRes.success || !enrichRes.data) {
          throw new Error(enrichRes.error ?? 'Enrichment failed')
        }
        // Seed the enrichment cache so the detail panel renders without
        // a second roundtrip.
        queryClient.setQueryData(enrichmentKeys.detail(lead.id), enrichRes.data.enrichment)
      } catch (err) {
        // Surface the error but proceed — the lead is created and the user
        // can re-run enrichment from the detail panel.
        setError(
          (err instanceof Error ? err.message : 'Enrichment failed') +
            '. Lead was added; you can retry enrichment from the detail panel.',
        )
        setStage('idle')
        // Still flush caches and open detail so the user isn't stuck.
        await queryClient.invalidateQueries({ queryKey: leadKeys.lists() })
        await queryClient.invalidateQueries({ queryKey: leadKeys.stats() })
        await queryClient.invalidateQueries({ queryKey: leadKeys.detail(lead.id) })
        onLeadCreated?.(lead)
        onOpenChange(false)
        reset()
        return
      }
    }

    // 3. Both ops succeeded — flush caches and open the detail panel. The
    // lead detail query will fetch the now-enriched row.
    await queryClient.invalidateQueries({ queryKey: leadKeys.lists() })
    await queryClient.invalidateQueries({ queryKey: leadKeys.stats() })
    await queryClient.invalidateQueries({ queryKey: leadKeys.detail(lead.id) })
    onLeadCreated?.(lead)
    onOpenChange(false)
    reset()
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="max-w-sm"
        onInteractOutside={(e) => { if (busy) e.preventDefault() }}
        onEscapeKeyDown={(e) => { if (busy) e.preventDefault() }}
      >
        <DialogHeader>
          <DialogTitle>Add by URL</DialogTitle>
          <DialogDescription>
            Paste a LinkedIn URL. We accept either a single profile
            (<span className="font-mono text-[11px]">/in/&hellip;</span>) or a
            people-search results page
            (<span className="font-mono text-[11px]">/search/results/people/&hellip;</span>). In that
            case we&apos;ll import every profile on that page.
          </DialogDescription>
        </DialogHeader>

        <form
          onSubmit={(e) => {
            e.preventDefault()
            void handleSubmit()
          }}
          className="flex flex-col gap-3"
        >
          <div className="flex flex-col gap-1.5">
            <div className="flex items-center gap-1">
              <Label htmlFor="lead-url" className="text-xs">LinkedIn URL</Label>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    className="inline-flex items-center text-muted-foreground hover:text-foreground"
                    aria-label="How to get a LinkedIn search URL"
                  >
                    <HelpCircle className="h-3 w-3" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="right" className="max-w-sm text-xs">
                  <div className="flex flex-col gap-2">
                    <p className="font-semibold">Two URL types work here:</p>
                    <div>
                      <p className="font-medium">1. A single profile</p>
                      <p className="font-mono text-[10px] mt-0.5 opacity-80">
                        linkedin.com/in/jane-doe/
                      </p>
                      <p className="mt-0.5">Adds that one person as a lead.</p>
                    </div>
                    <div>
                      <p className="font-medium">2. A people-search results URL</p>
                      <p className="font-mono text-[10px] mt-0.5 opacity-80">
                        linkedin.com/search/results/people/?keywords=…
                      </p>
                      <p className="mt-0.5">
                        Adds up to 50 profiles from page 1 of the search.
                      </p>
                    </div>
                    <div className="border-t border-border/40 pt-2">
                      <p className="font-medium">How to grab one:</p>
                      <ol className="ml-3 mt-0.5 list-decimal space-y-0.5">
                        <li>Open LinkedIn → top search bar.</li>
                        <li>Type your query (e.g. "VP Sales at fintech").</li>
                        <li>Click <span className="font-medium">People</span> filter.</li>
                        <li>Apply any filters you want (industry, location, company size).</li>
                        <li>Copy the full URL from the address bar.</li>
                      </ol>
                    </div>
                    <p className="text-muted-foreground italic">
                      Tip: filters are encoded in the URL. The more you set on LinkedIn, the
                      more targeted the import.
                    </p>
                  </div>
                </TooltipContent>
              </Tooltip>
            </div>
            <Input
              id="lead-url"
              type="url"
              placeholder="https://www.linkedin.com/in/jane-doe/  or  /search/results/people/?keywords=&hellip;"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              onBlur={() => setTouched(true)}
              autoFocus
              disabled={busy}
            />
            {showError && (
              <p className="text-xs text-destructive">
                Must be a linkedin.com profile URL or a people-search results URL.
              </p>
            )}
            {!showError && isSearchUrl && (
              <p className="inline-flex items-center gap-1 text-xs text-[var(--color-cobalt-bright)]">
                <SearchIcon className="h-3 w-3" />
                Search URL detected. Every profile on this page will be imported.
              </p>
            )}
            {!showError && !isSearchUrl && (
              <a
                href="https://www.linkedin.com/search/results/people/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 self-start text-[11px] text-[var(--color-cobalt-bright)] hover:underline"
              >
                <ExternalLink className="h-3 w-3" />
                Open LinkedIn people search
              </a>
            )}
          </div>

          {/* Research is automatic for every added lead (2026-09-17). Search
              imports research the imported rows in bulk (up to 25 per import)
              through /api/ai/enrich/bulk. */}
          <div className="rounded-md border bg-muted/30 p-3 text-xs text-muted-foreground">
            <span className="font-medium text-foreground">AI research runs automatically.</span>{' '}
            {isSearchUrl
              ? 'Up to 25 imported profiles get company info, recent news, pain points and a recommended angle. No credits used; counts toward your daily research allowance. About 10 to 20 seconds per lead.'
              : 'Company info, recent news, pain points and a recommended outreach strategy are pulled right after the lead is created. No credits used; counts toward your daily research allowance. About 10 to 20 seconds.'}
          </div>

          {stage === 'enriching' && (
            <div className="flex items-center gap-2 rounded-md bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              {isSearchUrl ? 'Researching the imported leads… this can take a few minutes for a full page.' : 'Researching… this can take 10–20 seconds.'}
            </div>
          )}

          {stage === 'importing' && (
            <div className="flex items-center gap-2 rounded-md bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              Importing leads from search results… this can take 30–60 seconds.
            </div>
          )}

          {importSummary && (
            <div className="rounded-md border border-[var(--color-cobalt)]/30 bg-[var(--color-cobalt)]/5 px-3 py-2 text-xs">
              <p className="font-medium text-[var(--color-silver-bright)]">
                Imported {importSummary.created} new lead{importSummary.created === 1 ? '' : 's'}.
              </p>
              {(importSummary.restored ?? 0) > 0 && (
                <p className="text-muted-foreground">
                  Restored {importSummary.restored} previously deleted lead{importSummary.restored === 1 ? '' : 's'}.
                </p>
              )}
              {importSummary.already_existed > 0 && (
                <p className="text-muted-foreground">
                  Skipped {importSummary.already_existed} already in your pipeline.
                </p>
              )}
              {importSummary.researched !== undefined && (
                <p className="text-muted-foreground">
                  Researched {importSummary.researched} lead{importSummary.researched === 1 ? '' : 's'}.
                </p>
              )}
              {importSummary.research_note && (
                <p className="text-amber-400">{importSummary.research_note}</p>
              )}
            </div>
          )}

          {error && (
            <p className="text-xs text-destructive">{error}</p>
          )}
        </form>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={() => void handleSubmit()} disabled={!looksValid || busy}>
            {stage === 'creating' ? (
              <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Adding...</>
            ) : stage === 'enriching' ? (
              <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Enriching...</>
            ) : stage === 'importing' ? (
              <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Importing...</>
            ) : isSearchUrl ? (
              <><SearchIcon className="h-4 w-4 mr-1" /> Import all & research</>
            ) : (
              'Add & Research'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
